import React, { useMemo } from 'react'
import TablePagination from '@mui/material/TablePagination'

export const MUI_TABLE_PAGINATION_OPTIONS = [10, 25, 50]
const MAX_ROWS_PER_PAGE = 50

const labelDisplayedRows = ({ from, to, count }) =>
  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`

const normalizeRowsPerPage = (rowsPerPage, options) => {
  const parsed = Number(rowsPerPage)
  if (options.includes(parsed)) return parsed
  return options.includes(25) ? 25 : options[0]
}

/**
 * MUI TablePagination wrapper with Spanish labels.
 * Accepts 1-based `page` to match existing app pagination state.
 * Max rows per page is 50.
 */
const MuiTablePagination = ({
  count = 0,
  page = 1,
  rowsPerPage = 25,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = MUI_TABLE_PAGINATION_OPTIONS,
  sx,
  ...rest
}) => {
  const options = useMemo(
    () =>
      (rowsPerPageOptions || MUI_TABLE_PAGINATION_OPTIONS)
        .map(Number)
        .filter((value) => value > 0 && value <= MAX_ROWS_PER_PAGE),
    [rowsPerPageOptions]
  )

  const safeRowsPerPage = normalizeRowsPerPage(rowsPerPage, options)

  const handlePageChange = (event, zeroBasedPage) => {
    onPageChange?.(event, zeroBasedPage + 1)
  }

  const handleRowsPerPageChange = (event) => {
    const nextRowsPerPage = normalizeRowsPerPage(event.target.value, options)
    onRowsPerPageChange?.(event, nextRowsPerPage)
  }

  return (
    <TablePagination
      component="div"
      count={count}
      page={Math.max(page - 1, 0)}
      onPageChange={handlePageChange}
      rowsPerPage={safeRowsPerPage}
      onRowsPerPageChange={handleRowsPerPageChange}
      rowsPerPageOptions={options}
      labelRowsPerPage="Filas por página:"
      labelDisplayedRows={labelDisplayedRows}
      slotProps={{
        select: {
          native: false,
          inputProps: { 'aria-label': 'Filas por página' }
        },
        actions: {
          previousButton: { disableRipple: false },
          nextButton: { disableRipple: false }
        }
      }}
      sx={{
        border: 0,
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        color: 'rgba(0, 0, 0, 0.87)',
        '.MuiToolbar-root': {
          minHeight: 52,
          height: 52,
          px: 1,
          pl: { sm: 1 },
          pr: { sm: 1 }
        },
        '.MuiTablePagination-spacer': {
          flex: '1 1 100%'
        },
        '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
          my: 0,
          fontSize: '0.875rem',
          color: 'rgba(0, 0, 0, 0.6)'
        },
        '.MuiTablePagination-input': {
          mr: 2,
          ml: 1
        },
        '.MuiTablePagination-select': {
          fontSize: '0.875rem',
          color: 'rgba(0, 0, 0, 0.87)',
          py: 0.5
        },
        '.MuiTablePagination-actions': {
          ml: 1.5
        },
        // Avoid Bootstrap/global button styles bleeding into MUI icons
        '.MuiTablePagination-actions .MuiIconButton-root': {
          color: 'rgba(0, 0, 0, 0.54)',
          backgroundColor: 'transparent !important',
          border: 'none !important',
          borderRadius: '50%',
          boxShadow: 'none !important',
          width: 40,
          height: 40,
          padding: 8,
          margin: 0,
          fontSize: 'inherit',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04) !important'
          },
          '&.Mui-disabled': {
            color: 'rgba(0, 0, 0, 0.26)',
            backgroundColor: 'transparent !important'
          }
        },
        ...sx
      }}
      {...rest}
    />
  )
}

export default MuiTablePagination
